import React, { useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { SEOHead } from '../components/SEOHead';
import { useProducts } from '../hooks/useProducts';
import { usePromoFilter } from '../hooks/usePromoFilter';
import { useProductFilters } from '../hooks/useProductFilters';
import { usePagination } from '../hooks/usePagination';
import { useReservationsContext } from '../context/ReservationsContext';
import { events } from '../analytics';
import ProductCard from '../components/ProductCard/ProductCard';
import ProductModal from '../components/ProductModal/ProductModal';
import ReservationModal from '../components/ReservationModal/ReservationModal';
import SearchBar from '../components/SearchBar/SearchBar';
import FilterBar from '../components/FilterBar';
import Pagination from '../components/Pagination/Pagination';
import PromoFilterBanner from '../components/PromoFilterBanner';
import {
    ReadyForYouContainer,
    SectionTitle,
    SectionSubtitle,
    SearchWrapper,
    ProductsGrid,
    NoResultsMessage,
    ResultsCount
} from './ReadyForYouPage.styled';

function ReadyForYouPage() {
    const { products, searchTerm, setSearchTerm } = useProducts();
    const { getProductStatus } = useReservationsContext();

    // Filtrar productos ocultos (status: 'hidden')
    const visibleProducts = useMemo(() => {
        return products.filter(product => getProductStatus(product.id) !== 'hidden');
    }, [products, getProductStatus]);

    // Aplicar filtro de promocion si viene de una promo (via URL param)
    const {
        filteredProducts: promoFilteredProducts,
        activePromo,
        isPromoFilterActive,
        clearPromoFilter
    } = usePromoFilter(visibleProducts);

    // Aplicar filtros de categoria, ordenamiento y promo toggle
    const {
        filteredProducts: finalFilteredProducts,
        activeCategory,
        activeSortId,
        promoOnly,
        hasActiveFilters,
        setCategory,
        setSort,
        togglePromoOnly,
        clearAllFilters,
        availableCategories,
        sortOptions,
        isPromoAvailable
    } = useProductFilters(promoFilteredProducts);

    const totalProducts = finalFilteredProducts.length;

    const {
        paginatedItems,
        currentPage,
        totalPages,
        nextPage,
        prevPage,
        hasNextPage,
        hasPrevPage
    } = usePagination(finalFilteredProducts, 6);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isReservationOpen, setIsReservationOpen] = useState(false);

    // Ref para el grid de productos
    const gridRef = useRef(null);

    // Función de scroll suave hacia el grid
    const scrollToGrid = () => {
        if (gridRef.current) {
            const headerOffset = 120; // Altura del header sticky + margen
            const elementPosition = gridRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    // Handlers que combinan paginación + scroll
    const handleNextPage = () => {
        nextPage();
        scrollToGrid();
    };

    const handlePrevPage = () => {
        prevPage();
        scrollToGrid();
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setIsGalleryOpen(true);
        events.productView(product.name, product.id);
    };

    const handleReserveClick = (product) => {
        setSelectedProduct(product);
        setIsReservationOpen(true);
        events.reservationStart(product.name, product.id);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        if (term.trim().length > 2) {
            events.productSearch(term);
        }
    };

    const handleCategoryChange = (categoryId) => {
        setCategory(categoryId);
        events.filterCategory(categoryId);
    };

    const handleSortChange = (sortId) => {
        setSort(sortId);
        events.filterSort(sortId);
    };

    const handlePromoToggle = () => {
        togglePromoOnly();
        events.filterPromoToggle(!promoOnly);
    };

    const handleClearFilters = () => {
        clearAllFilters();
        events.filterClear();
    };

    const handleCloseGallery = () => {
        setIsGalleryOpen(false);
    };

    const handleCloseReservation = () => {
        setIsReservationOpen(false);
    };

    const handleReserveFromGallery = () => {
        setIsGalleryOpen(false);
        setIsReservationOpen(true);
    };

    const modalRoot = document.getElementById('modal-root');

    return (
        <ReadyForYouContainer>
            <SEOHead
                title="Listos para Ti - Tienda Duo a Color"
                description="Piezas únicas de Duo a Color listas para comprar. Totebags y accesorios punch needle artesanales. Reserva tu diseño exclusivo hecho en España."
                path="/listos-para-ti"
            />
            <SectionTitle>Listos para Ti</SectionTitle>
            <SectionSubtitle>
                Piezas únicas ya creadas, esperando encontrar su hogar.
                Reserva la tuya antes de que alguien más lo haga.
            </SectionSubtitle>

            <SearchWrapper>
                <SearchBar
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Buscar productos..."
                />
            </SearchWrapper>

            <FilterBar
                activeCategory={activeCategory}
                activeSortId={activeSortId}
                promoOnly={promoOnly}
                availableCategories={availableCategories}
                sortOptions={sortOptions}
                onCategoryChange={handleCategoryChange}
                onSortChange={handleSortChange}
                onPromoToggle={handlePromoToggle}
                onClearAll={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                resultCount={totalProducts}
                isPromoAvailable={isPromoAvailable}
            />

            {isPromoFilterActive && (
                <PromoFilterBanner
                    promoTitle={activePromo.title}
                    productCount={totalProducts}
                    onClear={clearPromoFilter}
                />
            )}

            {searchTerm && (
                <ResultsCount>
                    {totalProducts} {totalProducts === 1 ? 'resultado' : 'resultados'} para "{searchTerm}"
                </ResultsCount>
            )}

            {paginatedItems.length > 0 ? (
                <>
                    <ProductsGrid ref={gridRef}>
                        {paginatedItems.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onImageClick={() => handleProductClick(product)}
                                onReserveClick={() => handleReserveClick(product)}
                            />
                        ))}
                    </ProductsGrid>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPrevPage={handlePrevPage}
                        onNextPage={handleNextPage}
                        hasPrevPage={hasPrevPage}
                        hasNextPage={hasNextPage}
                    />
                </>
            ) : (
                <NoResultsMessage>
                    {searchTerm
                        ? `No encontramos productos que coincidan con "${searchTerm}"`
                        : hasActiveFilters
                            ? 'No hay productos que coincidan con los filtros seleccionados'
                            : isPromoFilterActive
                                ? 'No hay productos disponibles en esta promoción'
                                : 'No hay productos disponibles en este momento'
                    }
                </NoResultsMessage>
            )}

            {/* Modal de Galería */}
            {selectedProduct && isGalleryOpen && modalRoot && ReactDOM.createPortal(
                <ProductModal
                    product={selectedProduct}
                    isOpen={isGalleryOpen}
                    onClose={handleCloseGallery}
                    onReserve={handleReserveFromGallery}
                />,
                modalRoot
            )}

            {/* Modal de Reserva */}
            {selectedProduct && isReservationOpen && modalRoot && ReactDOM.createPortal(
                <ReservationModal
                    product={selectedProduct}
                    isOpen={isReservationOpen}
                    onClose={handleCloseReservation}
                />,
                modalRoot
            )}
        </ReadyForYouContainer>
    );
}

export default ReadyForYouPage;
